<div class="tl-block">

  <!-- ── Header ──────────────────────────────────────────────────────────────── -->
  <div class="tl-header">
    <h2 class="tl-task-heading">Task linkage for {{ taskFields['control'] }}</h2>
    <div class="tl-intro-body ai-markdown" *ngIf="taskOverviewProse" [innerHTML]="proseHtml(taskOverviewProse)"></div>
  </div>

  <!-- ── Lineage tree ──────────────────────────────────────────────────────── -->
  <div class="lt-tree">
    <div class="lt-row" *ngFor="let row of lineageRows" [ngClass]="'lt-row--' + row.type">
      <span class="lt-pre" *ngFor="let c of row.continues" [class.lt-pre--pipe]="c"></span>
      <span class="lt-conn" *ngIf="row.depth > 0" [class.lt-conn--tee]="!row.isLast"></span>
      <span class="lt-lbl">{{ row.label }}</span>
      <span *ngIf="row.badge" class="ai-pill lt-bdg" [ngClass]="badgeClass(row.badge)">{{ row.badge }}</span>
    </div>
  </div>

  <!-- ── 1. Linked risk ──────────────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="riskRows.length > 0">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-risk">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('risk') }}. Linked risk</span>
      <span class="tl-section-badge">{{ block.totals?.riskCount ?? riskRows.length }}</span>
    </div>
    <div class="ai-markdown tl-section-hint" *ngIf="linkedRiskProse" [innerHTML]="proseHtml(linkedRiskProse)"></div>
    <div class="tl-risk-item" *ngFor="let row of riskRows">
      <div class="tl-risk-name">
        <span class="ai-mono">{{ row.riskId }}</span>
        &mdash; {{ row.title }}
      </div>
      <p class="tl-risk-desc" *ngIf="row.description !== row.title">{{ row.description }}</p>
      <div class="tl-risk-badges">
        <span *ngIf="row.riskLevel" class="ai-pill" [ngClass]="badgeClass(row.riskLevel)">{{ row.riskLevel }}</span>
        <span *ngIf="row.priority"  class="ai-pill" [ngClass]="badgeClass(row.priority)">{{ row.priority }}</span>
        <span *ngIf="row.status"    class="ai-pill" [ngClass]="badgeClass(row.status)">{{ row.status }}</span>
      </div>
    </div>
  </div>

  <!-- ── 2. Framework coverage ───────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="frameworks.length > 0">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-fw">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('frameworks') }}. Framework coverage</span>
    </div>
    <div class="tl-fw-pills">
      <span *ngFor="let fw of frameworks; let i = index" class="tl-fw-pill" [ngClass]="fwPillClass(i)">
        <span class="tl-fw-dot"></span>{{ fw }}
      </span>
    </div>
  </div>

  <!-- ── 3. Asset categories ─────────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="assetCategoryRows.length > 0">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-asset">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('categories') }}. Asset categories</span>
    </div>
    <div class="ai-task-table-wrap">
      <table class="ai-task-table tl-cat-table">
        <thead>
          <tr>
            <th>Category</th>
            <th class="tl-th-right">Count</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of assetCategoryRows">
            <td>{{ row.category }}</td>
            <td class="tl-td-right">{{ row.count }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ── 4. Action items ─────────────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="(block.linkedActionItems?.length ?? 0) > 0">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-action">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('actions') }}. Action items</span>
      <div class="tl-asset-mode-toggle">
        <button class="tl-mode-btn" [class.tl-mode-btn--active]="actionItemViewMode === 'chip'"  (click)="actionItemViewMode = 'chip'">Chips</button>
        <button class="tl-mode-btn" [class.tl-mode-btn--active]="actionItemViewMode === 'table'" (click)="actionItemViewMode = 'table'">Table</button>
      </div>
    </div>

    <app-action-item-display
      [items]="block.linkedActionItems ?? []"
      [mode]="actionItemViewMode"
      [maxRecords]="10"
      [total]="block.totals?.actionItemCount">
    </app-action-item-display>
  </div>

  <!-- ── 5. Linked assets ────────────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="(block.linkedAssets?.length ?? 0) > 0">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-asset">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('assets') }}. Linked assets</span>
      <div class="tl-asset-mode-toggle">
        <button class="tl-mode-btn" [class.tl-mode-btn--active]="assetViewMode === 'chip'"  (click)="assetViewMode = 'chip'">Chips</button>
        <button class="tl-mode-btn" [class.tl-mode-btn--active]="assetViewMode === 'table'" (click)="assetViewMode = 'table'">Table</button>
      </div>
    </div>

    <app-asset-display
      [assets]="block.linkedAssets ?? []"
      [mode]="assetViewMode"
      [maxRecords]="5"
      [total]="block.totals?.assetCount">
    </app-asset-display>
  </div>

  <!-- ── 6. Recommended next step ────────────────────────────────────────────── -->
  <div class="tl-section" *ngIf="nextStepProse">
    <div class="tl-section-heading">
      <span class="tl-section-icon tl-icon-next">&#9632;</span>
      <span class="tl-section-title">{{ sectionNum('next') }}. Recommended next step</span>
    </div>
    <div class="ai-markdown tl-section-hint" [innerHTML]="proseHtml(nextStepProse)"></div>
  </div>

</div>
